import { clock } from '@/services/clock';

import type { MessagePart, UIMessage } from '@tanstack/ai-client';
import type { PersistedMessage } from '../types/chat.types';

type LegacyPersistedMessage = {
  id: string;
  sender?: 'human' | 'ai';
  role?: 'user' | 'assistant';
  text?: unknown;
  content?: unknown;
  parts?: unknown;
};

const LEGACY_DISPLAY_TOOL_NAMES: Record<string, string> = {
  table: 'render_table',
  default: 'render_default',
  spot_chart: 'render_spot_chart',
  seasonal_chart: 'render_seasonal_chart',
  forward_chart: 'render_forward_chart',
  file_chart: 'render_file_chart',
  static_chart: 'render_static_chart'
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function legacyToolOutput(part: Record<string, unknown>): Record<string, unknown> {
  const output = { ...part };
  delete output.kind;
  delete output.display;
  delete output.tool_call_id;
  return output;
}

function normalizePart(part: unknown): MessagePart | null {
  if (!isRecord(part)) return null;

  if (typeof part.type === 'string') {
    // Persisted TanStack parts have already crossed the serialization boundary.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- local storage is untyped at runtime; known TanStack discriminants are preserved by the migration adapter.
    return part as MessagePart;
  }

  if (part.kind === 'text' && typeof part.text === 'string') {
    return { type: 'text', content: part.text };
  }

  if (
    part.kind === 'tool_result' &&
    typeof part.tool_call_id === 'string' &&
    typeof part.display === 'string'
  ) {
    return {
      type: 'tool-call',
      id: part.tool_call_id,
      name: LEGACY_DISPLAY_TOOL_NAMES[part.display] ?? part.display,
      arguments: '{}',
      state: 'complete',
      output: legacyToolOutput(part)
    };
  }

  return null;
}

function normalizeParts(parts: unknown): UIMessage['parts'] {
  if (!Array.isArray(parts)) return [];
  return parts.map(normalizePart).filter((part): part is MessagePart => part !== null);
}

export function toPersistedMessage(message: UIMessage, rating?: -1 | 0 | 1): PersistedMessage {
  return {
    id: message.id,
    sender: message.role === 'user' ? 'human' : 'ai',
    role: message.role === 'user' ? 'user' : 'assistant',
    timestamp: (message.createdAt ?? clock.date()).toISOString(),
    parts: message.parts,
    ...(rating !== undefined && { rating })
  };
}

export function fromPersistedMessage(record: PersistedMessage | LegacyPersistedMessage): UIMessage {
  const role = record.role ?? (record.sender === 'human' ? 'user' : 'assistant');
  let parts: UIMessage['parts'];

  if (Array.isArray(record.parts)) {
    parts = normalizeParts(record.parts);
  } else if ('content' in record && Array.isArray(record.content)) {
    parts = normalizeParts(record.content);
  } else if ('text' in record && typeof record.text === 'string') {
    parts = [{ type: 'text', content: record.text }];
  } else {
    parts = [];
  }

  return { id: record.id, role, parts };
}
